/* ===========================================================
   sync/provider.js — DocSync: one open doc, kept in sync

   The doc is a Yjs document (a CRDT): edits from anyone, in any
   order, combine the same way everywhere. The life of an edit:

     1. you type → Yjs update → written to this device (IndexedDB)
        within ~40 ms, marked "pending"
     2. sent to the doc's room in a batch (fast when others are here,
        a little lazier when you're alone)
     3. the room stores it and replies "ack" with its sequence
        number → no longer pending: it's safe online
     4. everyone else in the room gets it live

   Coming back after editing offline: before mixing in what others
   did meanwhile, we take three snapshots (where we split, the online
   version, this device's version) and run the same three-way merge
   as "Merge changes back". Where both changed the same spot, the doc
   keeps BOTH versions next to each other and we ask the person which
   to keep. Nothing is thrown away until they choose.

   Status for the UI: loading · connecting · saving · saved ·
   offline · offline-pending · blocked (storage full etc.)
   =========================================================== */

import * as Y from 'yjs';
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
import { T, framesFor, parseFrame, Assembler, LIMITS } from '../../shared/protocol.js';
import { merge3 } from '../../shared/merge3.js';
import { canon, tidy } from '../../shared/blocks.js';

export const LOAD = 'dp:load';
export const REMOTE = 'dp:remote';
export const RESOLVE = 'dp:resolve';

export class DocSync {
  /**
   * @param {object} o
   * @param {string} o.docId
   * @param {object} o.store      IDBStore / MemoryStore
   * @param {() => Promise<WebSocket>} o.connect  opens the live socket (null = this device only)
   * @param {string} o.dev        this device's id
   * @param {(doc: Y.Doc) => object} [o.toJSON]            Yjs → ProseMirror JSON (for offline merges)
   * @param {(doc: Y.Doc, json: object, origin) => void} [o.applyJSON]
   */
  constructor(o) {
    this.id = o.docId;
    this.store = o.store;
    this.connectFn = o.connect || null;
    this.dev = o.dev || 'dev';
    this.toJSON = o.toJSON || null;
    this.applyJSON = o.applyJSON || null;
    this.role = o.role || null;

    this.doc = new Y.Doc();
    this.awareness = new Awareness(this.doc);
    this.handlers = new Map();

    this.lastSeq = 0;
    this.pending = [];            // [{ k, data }] stored here, not yet acked online
    this.buffer = [];             // typed in the last few ms, not yet stored here
    this.inflight = new Map();    // batch id → { keys, at }
    this.batch = 0;
    this.peers = 1;
    this.ws = null;
    this.connected = false;
    this.synced = false;
    this.catchup = null;
    this.blocked = null;
    this.fatal = null;
    this.closed = false;
    this.retry = 0;
    this.asm = new Assembler(LIMITS.docBytes);

    this.onDocUpdate = (u, origin) => {
      if (origin === LOAD || origin === REMOTE) return;
      this.local(u);
    };
    this.doc.on('update', this.onDocUpdate);
    this.onAware = ({ added, updated, removed }, origin) => {
      if (origin === 'local' && this.synced) this.sendAware(added.concat(updated, removed));
    };
    this.awareness.on('update', this.onAware);
  }

  on(evt, fn) {
    if (!this.handlers.has(evt)) this.handlers.set(evt, new Set());
    this.handlers.get(evt).add(fn);
    return () => this.handlers.get(evt)?.delete(fn);
  }
  emit(evt, ...args) {
    for (const fn of this.handlers.get(evt) || []) {
      try { fn(...args); } catch (err) { console.error('[sync] listener failed', evt, err); }
    }
  }

  /* ===========================================================
     start: this device first, then the network
     =========================================================== */

  async start() {
    const meta = await this.store.getMeta(this.id);
    this.lastSeq = meta?.lastSeq || 0;
    const rows = await this.store.entries(this.id);
    let bad = 0;
    this.doc.transact(() => {
      for (const r of rows) {
        try { Y.applyUpdate(this.doc, r.data, LOAD); } catch { bad++; }
      }
    }, LOAD);
    if (bad) console.warn(`[sync] ${bad} damaged saved changes skipped`);
    this.pending = rows.filter(r => r.pending).map(r => ({ k: r.k, data: r.data }));
    this.loaded = true;
    this.emit('loaded', { empty: !rows.length, fromDevice: rows.length > 0 });
    this.status();

    const confirmed = rows.filter(r => !r.pending);
    if (confirmed.length > 150) {
      this.store.compactSynced(this.id, Y.mergeUpdates(confirmed.map(r => r.data)), confirmed[confirmed.length - 1].k)
        .catch(err => console.warn('[sync] local tidy-up failed', err));
    }
    if (this.connectFn) this.connect();
    return this;
  }

  /* ---------- local edits ---------- */

  local(u) {
    this.buffer.push(u);
    if (!this.bufTimer) this.bufTimer = setTimeout(() => this.flushLocal(), 40);
    this.status();
  }

  /** Write buffered edits to this device. Safe to call any time (page hide, close). */
  async flushLocal() {
    clearTimeout(this.bufTimer);
    this.bufTimer = null;
    if (!this.buffer.length) return;
    const parts = this.buffer;
    this.buffer = [];
    const data = parts.length === 1 ? parts[0] : Y.mergeUpdates(parts);
    try {
      const k = await this.store.addEntry(this.id, data, true, this.doc.clientID);
      this.pending.push({ k, data });
    } catch (err) {
      // couldn't save on this device: keep it in memory and still send it
      console.error('[sync] saving on this device failed', err);
      this.pending.push({ k: -(++this.batch), data });
      this.emit('local-error', err);
    }
    this.status();
    this.schedulePush();
  }

  schedulePush(delay = this.peers > 1 ? 90 : 600) {
    if (!this.synced || this.pushTimer || this.blocked) return;
    this.pushTimer = setTimeout(() => { this.pushTimer = null; this.push(); }, delay);
  }

  push() {
    if (!this.synced || !this.ws || this.blocked || this.role === 'view') return;
    const sending = new Set();
    for (const b of this.inflight.values()) for (const k of b.keys) sending.add(k);
    const todo = this.pending.filter(p => !sending.has(p.k));
    if (!todo.length) return;
    const take = [];
    let size = 0;
    for (const p of todo) {
      if (take.length && size + p.data.length > LIMITS.pushBytes / 2) break;
      take.push(p);
      size += p.data.length;
    }
    const data = take.length === 1 ? take[0].data : Y.mergeUpdates(take.map(p => p.data));
    const id = ++this.batch;
    this.inflight.set(id, { keys: take.map(p => p.k), at: Date.now() });
    try { for (const f of framesFor(T.PUSH, id, data)) this.ws.send(f); } catch { /* socket closing; resent on reconnect */ }
    if (todo.length > take.length) this.schedulePush(0);
    this.status();
  }

  async onAck(id, seq) {
    const b = this.inflight.get(id);
    if (!b) return;
    this.inflight.delete(id);
    const done = new Set(b.keys);
    this.pending = this.pending.filter(p => !done.has(p.k));
    await this.store.ack(b.keys.filter(k => k > 0));
    await this.bumpSeq(seq);
    this.status();
    if (this.pending.length) this.schedulePush();
  }

  async bumpSeq(seq) {
    if (seq > this.lastSeq) {
      this.lastSeq = seq;
      await this.store.setMeta(this.id, { lastSeq: seq });
    }
  }

  /* ---------- the network ---------- */

  async connect() {
    if (this.closed || this.ws || this.connecting || this.fatal || !this.connectFn) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { this.status(); return; }
    this.connecting = true;
    this.status();
    let ws;
    try {
      ws = await this.connectFn();
    } catch (err) {
      this.connecting = false;
      if (err?.status === 404 || err?.status === 403) {
        this.fail({ code: err.status === 404 ? 'gone' : 'denied', msg: err.message });
        return;
      }
      if (err?.data?.signedOut) { this.fail({ code: 'signedout', msg: err.message }); return; }
      this.status();
      this.reconnectLater();
      return;
    }
    if (this.closed) { try { ws.close(1000); } catch { /* fine */ } return; }
    this.ws = ws;
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => this.onOpen();
    // one message at a time, in order: a sequence number must never be recorded
    // before the edit it stands for is stored on this device
    this.inbox = Promise.resolve();
    ws.onmessage = e => {
      this.inbox = this.inbox.then(() => this.onMessage(e.data)).catch(err => console.error('[sync] message failed', err));
    };
    ws.onclose = e => this.onClose(e);
    ws.onerror = () => {};
  }

  onOpen() {
    this.connecting = false;
    this.connected = true;
    this.retry = 0;
    this.blocked = null;
    this.lastHeard = Date.now();
    // with edits waiting here, hold the catch-up until it's all in, then merge carefully
    this.hadPending = this.pending.length > 0 || this.buffer.length > 0;
    this.catchup = this.hadPending ? [] : null;
    this.ws.send(JSON.stringify({ t: 'hello', since: this.lastSeq, dev: this.dev, cids: [this.doc.clientID] }));
    clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => this.tick(), 20_000);
    this.status();
  }

  tick() {
    if (!this.ws) return;
    if (Date.now() - this.lastHeard > 55_000) { try { this.ws.close(4000, 'quiet'); } catch { /* gone */ } return; }
    try { this.ws.send(JSON.stringify({ t: 'ping' })); } catch { /* gone */ }
    // a batch the room never confirmed gets sent again (harmless if it did arrive)
    const stale = [...this.inflight.entries()].filter(([, b]) => Date.now() - b.at > 25_000);
    if (stale.length) { for (const [id] of stale) this.inflight.delete(id); this.push(); }
  }

  async onMessage(data) {
    this.lastHeard = Date.now();
    if (typeof data === 'string') {
      let m;
      try { m = JSON.parse(data); } catch { return; }
      switch (m.t) {
        case 'welcome': this.setRole(m.role); break;
        case 'synced': await this.onSynced(m); break;
        case 'ack': await this.onAck(m.id, m.seq); break;
        case 'compact': await this.onCompactRequest(m.upTo); break;
        case 'peers': this.peers = m.n; this.emit('peers', m); break;
        case 'joined': this.sendAware(null); break;
        case 'gone': removeAwarenessStates(this.awareness, (m.cids || []).filter(c => c !== this.doc.clientID), 'remote'); break;
        case 'role': this.setRole(m.role); break;
        case 'error': this.onServerError(m); break;
        default: break;
      }
      return;
    }
    const f = parseFrame(data);
    if (f.type === T.AWARE) {
      try { applyAwarenessUpdate(this.awareness, f.payload, 'remote'); } catch { /* ignore a bad cursor */ }
      return;
    }
    if (f.type !== T.UPDATE && f.type !== T.BASE) return;
    const payload = this.asm.push(f);
    if (!payload) return;
    if (this.catchup) this.catchup.push({ seq: f.id, data: payload });
    else await this.applyRemote([{ seq: f.id, data: payload }]);
  }

  async applyRemote(list) {
    this.doc.transact(() => {
      for (const e of list) {
        try { Y.applyUpdate(this.doc, e.data, REMOTE); } catch (err) { console.warn('[sync] skipped a damaged update', err); }
      }
    }, REMOTE);
    for (const e of list) await this.store.addEntry(this.id, e.data, false);
    await this.bumpSeq(Math.max(...list.map(e => e.seq)));
  }

  async onSynced(m) {
    const list = this.catchup;
    this.catchup = null;
    if (list?.length) {
      if (m.others && this.hadPending && this.toJSON && this.applyJSON) await this.reconcile(list);
      else await this.applyRemote(list);
    }
    this.synced = true;
    this.emit('synced', { seq: this.lastSeq });
    this.sendAware(null);
    this.push();
    this.status();
  }

  /** Edits from this device meet edits made online while it was away. */
  async reconcile(list) {
    const rows = await this.store.entries(this.id);
    const baseDoc = new Y.Doc();
    for (const r of rows) if (!r.pending) { try { Y.applyUpdate(baseDoc, r.data); } catch { /* skip */ } }
    const onlineDoc = new Y.Doc();
    Y.applyUpdate(onlineDoc, Y.encodeStateAsUpdate(baseDoc));
    for (const e of list) { try { Y.applyUpdate(onlineDoc, e.data); } catch { /* skip */ } }

    const base = this.toJSON(baseDoc);
    const online = this.toJSON(onlineDoc);
    const mine = this.toJSON(this.doc);
    await this.applyRemote(list);

    const r = merge3(base, online, mine);
    if (!r.conflicts.length) return;
    // Lay clashing spots out as "online version, then yours" instead of a word-by-word jumble.
    this.applyJSON(this.doc, r.doc, RESOLVE);
    const rec = { at: Date.now(), conflicts: r.conflicts };
    await this.store.setConflicts(this.id, rec);
    this.emit('conflicts', rec);
  }

  /**
   * Apply the person's choices for offline clashes:
   * { [conflictId]: 'ours' (online) | 'theirs' (this device) | 'both' }.
   * Returns ids that couldn't be found any more (edited again since).
   */
  async resolveConflicts(choices) {
    const rec = await this.store.getConflicts(this.id);
    if (!rec) return [];
    let cur = this.toJSON(this.doc);
    const missed = [];
    for (const c of rec.conflicts) {
      const pick = choices[c.id];
      if (!pick || pick === 'both') continue;
      const keep = pick === 'ours' ? c.ours : pick === 'theirs' ? c.theirs : c.base;
      const res = replaceRun(cur, [...c.ours, ...c.theirs].map(canon), keep);
      if (res) cur = res; else missed.push(c.id);
    }
    this.applyJSON(this.doc, tidy(cur), RESOLVE);
    await this.store.clearConflicts(this.id);
    return missed;
  }

  async onCompactRequest(upTo) {
    if (this.role === 'view' || !this.synced || upTo > this.lastSeq || !this.ws) return;
    const rows = (await this.store.entries(this.id)).filter(r => !r.pending);
    if (!rows.length) return;
    const tmp = new Y.Doc();
    for (const r of rows) { try { Y.applyUpdate(tmp, r.data); } catch { return; } }
    const state = Y.encodeStateAsUpdate(tmp);
    try { for (const f of framesFor(T.COMPACT, upTo, state)) this.ws.send(f); } catch { return; }
    await this.store.compactSynced(this.id, state, rows[rows.length - 1].k);
  }

  onServerError(m) {
    if (m.code === 'readonly') { this.setRole('view'); return; }
    if (m.code === 'quota' || m.code === 'doc-full' || m.code === 'too-big') {
      this.blocked = { code: m.code, msg: m.msg };
      if (m.id) this.inflight.delete(m.id);
      this.emit('blocked', this.blocked);
      this.status();
      // try again later: space may have been freed
      clearTimeout(this.unblockTimer);
      this.unblockTimer = setTimeout(() => { this.blocked = null; this.push(); this.status(); }, 60_000);
      return;
    }
    if (m.code === 'deleted' || m.code === 'removed') { this.fail({ code: m.code, msg: m.msg }); return; }
    console.warn('[sync] server said', m);
  }

  setRole(role) {
    if (role === this.role) return;
    this.role = role;
    this.emit('role', role);
  }

  fail(err) {
    this.fatal = err;
    this.emit('fatal', err);
    this.close();
    this.status();
  }

  onClose(e) {
    this.ws = null;
    this.connected = false;
    this.connecting = false;
    this.synced = false;
    this.catchup = null;
    this.inflight.clear();
    clearInterval(this.pingTimer);
    const others = [...this.awareness.getStates().keys()].filter(c => c !== this.doc.clientID);
    if (others.length) removeAwarenessStates(this.awareness, others, 'remote');
    this.peers = 1;
    this.emit('peers', { n: 1, names: [] });
    this.status();
    if (e?.code === 4004) return this.fail({ code: 'deleted', msg: 'This doc was deleted.' });
    if (e?.code === 4003) return this.fail({ code: 'removed', msg: 'You no longer have access to this doc.' });
    this.reconnectLater();
  }

  reconnectLater() {
    if (this.closed || this.fatal) return;
    clearTimeout(this.retryTimer);
    const delay = Math.min(30_000, 1000 * 2 ** this.retry++) * (0.7 + Math.random() * 0.6);
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  /** The browser says we're back online. */
  online() {
    clearTimeout(this.retryTimer);
    this.retry = 0;
    this.connect();
  }

  /** The browser says we're offline: drop the socket now instead of waiting for it to time out. */
  offline() {
    const ws = this.ws;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      try { ws.close(4001, 'offline'); } catch { /* gone */ }
      this.onClose({ code: 4001 });
    }
    this.status();
  }

  sendAware(clients) {
    if (!this.ws || !this.synced) return;
    const ids = clients || [this.doc.clientID];
    try {
      const payload = encodeAwarenessUpdate(this.awareness, ids);
      this.ws.send(framesFor(T.AWARE, 0, payload)[0]);
    } catch { /* not connected */ }
  }

  /* ---------- status ---------- */

  unsynced() { return this.pending.length + this.buffer.length; }

  status() {
    const unsynced = this.unsynced();
    let state;
    if (this.fatal) state = 'gone';
    else if (!this.loaded) state = 'loading';
    else if (this.blocked) state = 'blocked';
    else if (this.synced) state = unsynced ? 'saving' : 'saved';
    else if (this.connecting || this.connected) state = unsynced ? 'saving' : 'connecting';
    else state = unsynced ? 'offline-pending' : 'offline';
    const s = { state, unsynced, peers: this.peers, blocked: this.blocked, fatal: this.fatal, role: this.role };
    const key = `${state}|${unsynced > 0}|${this.peers}|${this.role}`;
    if (key !== this.lastStatus) { this.lastStatus = key; this.emit('status', s); }
    return s;
  }

  close() {
    clearTimeout(this.retryTimer);
    clearTimeout(this.pushTimer);
    clearTimeout(this.unblockTimer);
    clearInterval(this.pingTimer);
    this.pushTimer = null;
    if (this.ws) { try { this.ws.close(1000, 'bye'); } catch { /* gone */ } }
    this.ws = null;
  }

  async destroy() {
    this.closed = true;
    await this.flushLocal();
    this.close();
    this.awareness.off('update', this.onAware);
    this.awareness.destroy();
    this.doc.off('update', this.onDocUpdate);
    this.handlers.clear();
  }
}

/** Find a run of blocks (by canon key) anywhere in the doc and swap it for `keep`. */
function replaceRun(node, keys, keep) {
  if (!node.content) return null;
  const ks = node.content.map(canon);
  for (let i = 0; i + keys.length <= ks.length; i++) {
    let match = true;
    for (let j = 0; j < keys.length; j++) if (ks[i + j] !== keys[j]) { match = false; break; }
    if (match) {
      const content = node.content.slice();
      content.splice(i, keys.length, ...keep);
      return { ...node, content };
    }
  }
  for (let i = 0; i < node.content.length; i++) {
    const res = replaceRun(node.content[i], keys, keep);
    if (res) {
      const content = node.content.slice();
      content[i] = res;
      return { ...node, content };
    }
  }
  return null;
}
